import React, { createRef, useEffect, useState } from 'react';
import { Row, Col, Button, Input, Form, FormGroup, Container, FormFeedback } from 'reactstrap';
import html2canvas from 'html2canvas';

import AppModal from 'components/app-modal.component';
import { IApprovedTimesheet } from 'features/pending-timesheets/models/approved-timesheet.model';
import { TypedLocalState } from 'utils/redux-hooks';

export interface IApproveModalProps {
    selectedTimesheetCount: number;
    selectedTimesheetIds: string[];
    confirmationEmail: string;
    isOpen: boolean;
    close: () => void;
    approve: (approvedTimesheets: IApprovedTimesheet[], supervisorSignature: string, supervisorName: string) => void;
}

const ApproveModal = (props: IApproveModalProps): JSX.Element => {
    const [image, setImage]: TypedLocalState<string> = useState('');
    const [fullName, setFullName]: TypedLocalState<string> = useState('');
    const [submitApproval, setSubmitApproval]: TypedLocalState<boolean> = useState(false);
    const sigRef = createRef<HTMLDivElement>();
    const modalRef = createRef<HTMLDivElement>();

    const approvedTimesheets: IApprovedTimesheet[] = props.selectedTimesheetIds.map((selectedTimesheetId: string) => ({
        id: selectedTimesheetId,
        supervisorSignature: '',
        supervisorSignatureDate: new Date(),
        supervisorName: '',
        supervisorEmail: '',
        supervisorPhone: '',
        onlineApprovalEmail: '',
        supervisorComment: '',
        onlineApprovalDate: new Date(),
    }));

    const getImage = () => {
        if (sigRef?.current) {
            modalRef.current.scrollTop = 0;

            html2canvas(sigRef.current).then((canvas) => {
                const imgData = canvas.toDataURL('image/png');
                setImage(imgData);
            });
        }
    };

    useEffect(() => {
        getImage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitApproval]);

    useEffect(() => {
        if (image && submitApproval) {
            props.approve(approvedTimesheets, image, fullName);
            setFullName('');
            setSubmitApproval(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [image]);

    const renderHeader = (): JSX.Element => {
        const s = props?.selectedTimesheetCount === 1 ? '' : 's';
        return (
            <span>
                Submittal approval for {props?.selectedTimesheetCount} timesheet{s}
            </span>
        );
    };

    const renderFooter = (): JSX.Element => {
        return (
            <Row className="w-100">
                <Col className="align-items-end d-flex">
                    <Button
                        color="light"
                        className="rounded-pill mr-2 ml-auto"
                        onClick={() => {
                            props.close();
                            setFullName('');
                            setSubmitApproval(false);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        id="approve-button"
                        disabled={fullName === '' || props.selectedTimesheetCount <= 0}
                        color="primary"
                        className="rounded-pill"
                        onClick={() => setSubmitApproval(true)}
                    >
                        Submit
                    </Button>
                </Col>
            </Row>
        );
    };

    return (
        <AppModal
            header={renderHeader()}
            footer={renderFooter()}
            isOpen={props.isOpen}
            close={props.close}
            size={'md'}
            innerRef={modalRef}
        >
            <Container>
                <Form
                    onSubmit={(e) => {
                        setSubmitApproval(true);
                        e.preventDefault();
                    }}
                >
                    <FormGroup className={'form-group-approve'}>
                        <label className="sr-only" htmlFor="approverName">
                            Full Name (required)
                        </label>
                        <Input
                            valid={fullName !== ''}
                            invalid={fullName === ''}
                            required
                            aria-label={'Full Name'}
                            type="text"
                            name="approverName"
                            id="approverName"
                            placeholder="Full Name"
                            onChange={(e: React.FormEvent<HTMLInputElement>) => {
                                setFullName(e?.currentTarget?.value.trim());
                                approvedTimesheets.forEach(
                                    (at: IApprovedTimesheet) => (at.supervisorName = e?.currentTarget?.value),
                                );
                            }}
                        />
                    </FormGroup>
                    <div className={'signature__wrapper'}>
                        <div ref={sigRef} className={'h3 signature text-center font-script'}>
                            <p className={'d-block'}>&nbsp;{fullName}&nbsp;</p>
                        </div>
                    </div>

                    <FormGroup className={'form-group-approve'}>
                        <FormFeedback>Please sign</FormFeedback>
                        <Row>
                            <Col className={'text-center confirmation-text-color'}>
                                <span className="font-weight-normal">
                                    {/* { TODO: temporarily commented out until we get confirmationEmail from endpoint} */}
                                    {/* {'A confirmation email will be sent to ' + props.confirmationEmail} */}
                                    You'll receive a confirmation email from timesheets@medicalsolutions.com
                                </span>
                            </Col>
                        </Row>
                    </FormGroup>
                </Form>
            </Container>
        </AppModal>
    );
};

export default ApproveModal;
